import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useAffiliateMetadata } from 'utils/affiliateHelpers';
import {
  referralCodes,
  ringReferralCodes,
  ringProReferralCodes,
  getRingPromoDiscountConfig,
  getHomePromoDiscountConfig,
} from 'content/referral';
import { Campaign, getCampaignByProduct } from 'content/campaigns';
import { isM1UhxBundlingEnabled } from 'content/products/m1/m1UhxBundlingConfig';
import { ultrahumanXDiscountCodes } from 'content/ultrahumanX';
import {
  ClueShopifyMap,
  isClueAvailable,
} from 'content/products/addons/clue';
import {
  LUMINARY_FLOW_ENABLED,
  LuminaryFlowShopifyMap,
} from 'content/products/addons/luminaryFlow';
import { getHeroDiscountCode, HeroCategoriesMap } from '../utils';
import { isGovxEligible } from 'components/heroes/govxConfig';
import { ProductType } from '../types';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { DiscountManager } from './types';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  cartHasHomeLine,
  cartHasM1Line,
  cartHasOneTimeM1Line,
  shouldApplyHomeCampaign,
  shouldApplyM1Campaign,
} from 'lib/commerce/cart/campaignLinePresence';

export const useDiscountManager = (
  store: ShopifyStore | null,
  cartType: ProductType | undefined,
  ultrahumanXAdded: any,
  cart: Record<string, any> | null,
  discountCodesRef: React.MutableRefObject<Array<string> | []>,
  campaigns: Campaign[] = [],
): DiscountManager => {
  const searchParams = useSearchParams();
  const internalPathname = useInternalPathname();
  const affiliateCode = searchParams?.get('affiliateCode');
  const { region: country } = useRegionLocale();

  // Don't load affiliate details if it's just a birthday discount without a valid discount parameter
  const shouldLoadAffiliateDetails =
    affiliateCode &&
    !(affiliateCode === 'birthday' && !searchParams?.get('discount'));

  const { affiliateDetails } = useAffiliateMetadata(
    shouldLoadAffiliateDetails ? (affiliateCode as string) : '',
  );

  const campaignActive = false; // Change when adding a campaign for uhx

  const setDiscountCodes = useCallback(
    (
      initialDiscounts: Array<string>,
      options: {
        isM1ProductTypePresent?: boolean;
        isRingProductTypePresent?: boolean;
        isRingProProductTypePresent?: boolean;
        isUhxProductTypePresent?: boolean;
        isHomeProductTypePresent?: boolean;
        isOneTimeM1Present?: boolean;
        removingUhx?: boolean;
        hasTradeIn?: boolean;
      } = {},
    ): Array<string> => {
      if (!store) {
        discountCodesRef.current = [];
        return discountCodesRef.current;
      }

      const {
        isM1ProductTypePresent,
        isRingProductTypePresent,
        isRingProProductTypePresent,
        isUhxProductTypePresent,
        isHomeProductTypePresent,
        isOneTimeM1Present,
        removingUhx = false,
        hasTradeIn = false,
      } = options;

      // Ultrahuman × Luminary flow (Ring PRO, US only). Its 19% is a ring-level
      // discount that must not stack with the other ring-level codes, so it
      // suppresses the referral/affiliate/hero pushes below. This mirrors the
      // suppression in RingProSelectionContext — the page total and the Shopify
      // total have to agree.
      const isLuminaryFlow =
        LUMINARY_FLOW_ENABLED &&
        searchParams?.get('flow') === 'luminary' &&
        store === ShopifyStore.US &&
        (cartType === ProductType.RING_PRO ||
          isRingProProductTypePresent ||
          internalPathname.startsWith('/ring-pro'));

      let discounts: Array<string> = [...initialDiscounts];

      // Add discount from URL query
      const queryDiscount = searchParams?.get('discount');
      if (queryDiscount && !isLuminaryFlow) {
        discounts.push(queryDiscount);
      }

      const referral = searchParams?.get('referral') ?? '';

      // Handle referral codes
      if (isLuminaryFlow) {
        // no-op: Luminary supplies the only ring-level code in this flow
      } else if (referral) {
        if (
          cartType === ProductType.RING_PRO ||
          isRingProProductTypePresent ||
          internalPathname.startsWith('/ring-pro')
        ) {
          const coupon =
            ringProReferralCodes[store as ShopifyStore]?.discountCode;
          if (coupon) {
            discounts = discounts.filter(
              (x) =>
                x !== ringReferralCodes[store as ShopifyStore]?.discountCode &&
                x !== referralCodes[store as ShopifyStore]?.discountCode,
            );
            discounts.push(coupon);
          }
        } else if (cartType === ProductType.RING || isRingProductTypePresent) {
          const coupon = ringReferralCodes[store as ShopifyStore].discountCode;
          discounts = discounts.filter(
            (x) => x !== referralCodes[store as ShopifyStore]?.discountCode,
          );
          discounts.push(coupon);
        } else if (cartType === ProductType.M1 || isM1ProductTypePresent) {
          const coupon =
            referralCodes[store as ShopifyStore]?.discountCode ?? '';
          discounts = discounts.filter(
            (x) => x !== ringReferralCodes[store as ShopifyStore].discountCode,
          );
          discounts.push(coupon);
        }
      } else if (affiliateDetails) {
        // Handle affiliate discounts — only push the code for the current product
        const effectiveStore = getEffectiveStoreForDiscount(store);

        const discountConfigs =
          affiliateDetails.discount_configs[effectiveStore as ShopifyStore] ??
          {};

        // Map current product context to the affiliate discount config key
        let affiliateCode_: string | undefined;
        if (cartType === ProductType.RING_PRO || isRingProProductTypePresent || internalPathname.startsWith('/ring-pro')) {
          affiliateCode_ = discountConfigs['RING_PRO']?.code;
        } else if (cartType === ProductType.RING || isRingProductTypePresent || internalPathname.startsWith('/ring')) {
          affiliateCode_ = discountConfigs['RING']?.code;
        } else if (cartType === ProductType.M1 || isM1ProductTypePresent || internalPathname.startsWith('/blood')) {
          affiliateCode_ = discountConfigs['M1']?.code;
        } else if (
          internalPathname.startsWith('/home') ||
          isHomeProductTypePresent ||
          cartHasHomeLine(cart, store)
        ) {
          affiliateCode_ = discountConfigs['HOME']?.code;
        }
        if (affiliateCode_) {
          discounts.push(affiliateCode_);
        }
      }

      const m1UhxBundlingEnabled = isM1UhxBundlingEnabled(store);
      const m1InCart = cartHasM1Line(cart) || isM1ProductTypePresent === true;
      const oneTimeM1 =
        cartHasOneTimeM1Line(cart) || isOneTimeM1Present === true;
      const uhxInCart = Boolean(ultrahumanXAdded) || isUhxProductTypePresent === true;
      // UHX replaces the CGM code while both are in the cart. Gate on the
      // lines, not cartType: a ring makes cartType RING and would stack both.
      const applyingUhxBundle =
        Boolean(store) &&
        m1UhxBundlingEnabled &&
        uhxInCart &&
        m1InCart &&
        !removingUhx;
      const uhxOverridesCampaign = applyingUhxBundle;

      if (applyingUhxBundle && store) {
        const uhxCode = ultrahumanXDiscountCodes[store]?.discountCode;
        if (uhxCode && !discounts.includes(uhxCode)) {
          discounts.push(uhxCode);
        }
      } else if (store && removingUhx) {
        const uhxCode = ultrahumanXDiscountCodes[store]?.discountCode;
        if (uhxCode) {
          discounts = discounts.filter((x) => x !== uhxCode);
        }
      }

      // Handle hero category discounts
      const heroCategory = searchParams?.get('heroCategory') ?? '';
      if (heroCategory && !isLuminaryFlow) {
        const blockVeteransForGovx =
          heroCategory === HeroCategoriesMap.VETERANS && isGovxEligible(country);
        if (!blockVeteransForGovx) {
          const heroCode = getHeroDiscountCode(heroCategory);
          if (heroCode) discounts.push(heroCode);
        }
      }

      // Handle birthday discount
      const birthdayDiscountCode = searchParams?.get('discount') ?? '';
      if (birthdayDiscountCode && !isLuminaryFlow) {
        discounts.push(birthdayDiscountCode);
      }

      // Handle Clue discount
      const isClueFlow =
        searchParams?.get('flow') === 'clue' && isClueAvailable(store);
      if (isClueFlow && store && ClueShopifyMap[store]?.Year1?.discountCode) {
        discounts.push(ClueShopifyMap[store].Year1.discountCode);
      }

      // Handle Luminary flow discount — 19% off the Ring PRO
      if (isLuminaryFlow && LuminaryFlowShopifyMap[store]?.discountCode) {
        discounts.push(LuminaryFlowShopifyMap[store].discountCode);
      }

      // DISABLED: Respiratory Health PowerPlug discount — re-enable by uncommenting
      // // Handle Respiratory discount
      // const isRespiratoryFlow = searchParams?.get('flow') === 'respiratory';
      // if (
      //   isRespiratoryFlow &&
      //   store &&
      //   RespiratoryShopifyMap[store]?.Year1?.discountCode
      // ) {
      //   discounts.push(RespiratoryShopifyMap[store].Year1.discountCode);
      // }

      const promoCountry = searchParams?.get('locale') ?? country;
      const ringPromoConfig = getRingPromoDiscountConfig({
        store,
        country: promoCountry,
      });

      if (
        store &&
        (cartType === ProductType.RING || isRingProductTypePresent) &&
        ringPromoConfig?.active &&
        ringPromoConfig?.discountCode
      ) {
        // const coupon = ringPromoConfig.discountCode;
        // if (coupon && !discounts.includes(coupon)) {
        //   discounts.push(coupon);
        // }
      }

      // --- Home Promo Discount ---
      const homePromoConfig = getHomePromoDiscountConfig({
        store,
        country: searchParams?.get('country'),
      });

      if (
        internalPathname.startsWith('/home') &&
        homePromoConfig?.active &&
        homePromoConfig.discountCode
      ) {
        // const coupon = homePromoConfig.discountCode;
        // if (coupon && !discounts.includes(coupon)) {
        //   discounts.push(coupon);
        // }
      }

      // --- Campaign Discount Codes ---
      const ringCampaign = getCampaignByProduct(campaigns, 'ring');
      if (
        ringCampaign &&
        !hasTradeIn &&
        (cartType === ProductType.RING || isRingProductTypePresent)
      ) {
        const coupon = ringCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const ringProCampaign = getCampaignByProduct(campaigns, 'ring-pro');
      if (
        ringProCampaign &&
        !hasTradeIn &&
        (cartType === ProductType.RING_PRO || isRingProProductTypePresent)
      ) {
        const coupon = ringProCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const homeCampaign = getCampaignByProduct(campaigns, 'home');
      if (
        homeCampaign &&
        shouldApplyHomeCampaign({
          pathname: internalPathname,
          cartHasHome: cartHasHomeLine(cart, store),
          isHomeProductTypePresent,
        })
      ) {
        const coupon = homeCampaign.discount_code;
        if (coupon && !discounts.includes(coupon)) {
          discounts.push(coupon);
        }
      }

      const m1Campaign = getCampaignByProduct(campaigns, 'cgm');
      const m1Coupon = m1Campaign?.discount_code;
      if (m1Coupon && uhxOverridesCampaign) {
        discounts = discounts.filter((code) => code !== m1Coupon);
      }
      if (
        m1Campaign &&
        shouldApplyM1Campaign({
          oneTimeM1,
          uhxOverridesCampaign,
        })
      ) {
        if (m1Coupon && !discounts.includes(m1Coupon)) {
          discounts.push(m1Coupon);
        }
      }

      // Remove duplicates and empty strings
      discountCodesRef.current = [...new Set(discounts.filter((x) => x))];

      return discountCodesRef.current;
    },
    [
      searchParams,
      internalPathname,
      store,
      cartType,
      cart,
      ultrahumanXAdded,
      affiliateDetails,
      campaignActive,
      country,
      campaigns,
    ],
  );

  const applicableDiscountCoupons = useMemo(
    () =>
      cart?.discountCodes
        ?.filter((x: { applicable: boolean }) => x.applicable)
        ?.map((x: { code: boolean }) => x.code) ?? [],
    [cart?.discountCodes],
  );

  return {
    discountCodes: discountCodesRef.current,
    setDiscountCodes,
    applicableDiscountCoupons,
  };
};
